import type { ComponentType } from 'react';
import {
  getPatternKeyForSourceViewType,
} from '@shared/types/view-registry';
import type {
  AnyViewEnvelope,
  DashboardViewFallbackReason,
  ViewDefinition,
  ViewPatternDataMap,
  ViewPatternKey,
  ViewEnvelope,
  ViewType,
} from '@shared/types/views';
import {
  DASHBOARD_VIEW_FALLBACK_REASONS,
  isViewPatternKey,
  isViewType,
} from '@shared/types/views';
import { FallbackPanelView } from '@/components/views/FallbackPanelView';
import {
  getFrontendViewPatternRegistration,
} from '@/components/views/pattern-registry';

const VIEW_ROUTER_FALLBACK_REASONS = [
  'pattern_missing',
  'pattern_unregistered',
  'view_payload_missing',
  'view_payload_invalid',
  'view_payload_mismatch',
  'dashboard_view_payload_missing',
  'ad_hoc_view_payload_invalid',
] as const;

type ViewRouterFallbackReason = typeof VIEW_ROUTER_FALLBACK_REASONS[number];

interface ViewRouterProps {
  hideTitle?: boolean;
  titleOverride?: string;
  viewType?: ViewType | null;
  viewDefinition?: ViewDefinition | null;
  payload?: ViewEnvelope | AnyViewEnvelope | null;
  rawPayload?: unknown;
  fallbackReason?: ViewRouterFallbackReason | null;
}

type ResolvedViewPattern = {
  patternKey: ViewPatternKey | null;
  reason: ViewRouterFallbackReason | null;
};

const isViewRouterFallbackReason = (value: unknown): value is ViewRouterFallbackReason => (
  typeof value === 'string'
  && VIEW_ROUTER_FALLBACK_REASONS.includes(value as ViewRouterFallbackReason)
);

const isDashboardViewFallbackReason = (value: unknown): value is DashboardViewFallbackReason => (
  typeof value === 'string'
  && DASHBOARD_VIEW_FALLBACK_REASONS.includes(value as DashboardViewFallbackReason)
);

const isEnvelopeLike = (value: unknown): value is AnyViewEnvelope => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    isViewType(candidate.view_type)
    && typeof candidate.schema_version === 'string'
    && candidate.data !== null
    && typeof candidate.data === 'object'
    && !Array.isArray(candidate.data)
  );
};

const isCanonicalDashboardFallbackEnvelope = (value: unknown): value is Extract<AnyViewEnvelope, { view_type: 'fallback_panel' }> => (
  isEnvelopeLike(value)
  && value.view_type === 'fallback_panel'
  && (value.data.reason === undefined || isDashboardViewFallbackReason(value.data.reason))
);

const resolvePatternKey = (params: {
  viewDefinition?: ViewDefinition | null;
  viewType?: ViewType | null;
  payload?: ViewEnvelope | AnyViewEnvelope | null;
}): ResolvedViewPattern => {
  if (params.viewDefinition && isViewPatternKey(params.viewDefinition.patternKey)) {
    return {
      patternKey: params.viewDefinition.patternKey,
      reason: null,
    };
  }

  if (params.viewType && isViewType(params.viewType)) {
    return {
      patternKey: getPatternKeyForSourceViewType(params.viewType),
      reason: null,
    };
  }

  if (!params.payload) {
    return {
      patternKey: null,
      reason: 'view_payload_missing',
    };
  }

  if (!isEnvelopeLike(params.payload)) {
    return {
      patternKey: null,
      reason: 'view_payload_invalid',
    };
  }

  return {
    patternKey: getPatternKeyForSourceViewType(params.payload.view_type),
    reason: null,
  };
};

const buildFallbackData = (params: {
  reason: ViewRouterFallbackReason;
  payload: unknown;
  patternKey?: ViewPatternKey | null;
  envelopeViewType?: ViewType | null;
}): ViewPatternDataMap['fallback_empty'] => {
  const messageByReason: Record<ViewRouterFallbackReason, string> = {
    pattern_missing: 'Le rendu du résultat doit être préparé.',
    pattern_unregistered: 'Le rendu du résultat n’est pas encore disponible.',
    view_payload_missing: 'Aucun résultat affichable n’est encore disponible.',
    view_payload_invalid: 'Le résultat est incomplet pour cet affichage.',
    view_payload_mismatch: 'Le résultat ne correspond pas encore à l’affichage attendu.',
    dashboard_view_payload_missing: 'Ce résultat n’est pas encore prêt à être affiché.',
    ad_hoc_view_payload_invalid: 'Le résultat doit être stabilisé avant affichage.',
  };

  const keyValues: ViewPatternDataMap['fallback_empty']['key_values'] = [];
  if (params.patternKey) {
    keyValues.push({ key: 'patternKey', value: params.patternKey });
  }
  if (params.envelopeViewType) {
    keyValues.push({ key: 'envelopeViewType', value: params.envelopeViewType });
  }

  let rawPreviewJson: string | undefined;
  if (params.payload !== undefined) {
    try {
      rawPreviewJson = JSON.stringify(params.payload, null, 2).slice(0, 4_000);
    } catch {
      rawPreviewJson = '[unserializable payload]';
    }
  }

  return {
    title: 'Résultat indisponible',
    message: messageByReason[params.reason],
    reason: params.reason,
    ...(keyValues.length > 0 ? { key_values: keyValues } : {}),
    ...(rawPreviewJson === undefined ? {} : { raw_preview_json: rawPreviewJson }),
  };
};

/** Every view sits in a size container, so it adapts to the width of its space. */
export const ViewRouter = (props: ViewRouterProps) => (
  <div className="view-container min-w-0"><ViewRouterContent {...props} /></div>
);

const ViewRouterContent = ({
  viewType,
  viewDefinition,
  payload,
  rawPayload,
  fallbackReason,
  hideTitle,
  titleOverride,
}: ViewRouterProps) => {
  const resolvedPattern = resolvePatternKey({
    viewDefinition,
    viewType,
    payload,
  });

  if (!resolvedPattern.patternKey) {
    const reason = isViewRouterFallbackReason(fallbackReason)
      ? fallbackReason
      : (resolvedPattern.reason ?? 'pattern_missing');

    return (
      <FallbackPanelView
        data={buildFallbackData({
          reason,
          payload: rawPayload ?? payload,
        })}
      />
    );
  }

  const registration = getFrontendViewPatternRegistration(resolvedPattern.patternKey);
  if (!registration) {
    return (
      <FallbackPanelView
        data={buildFallbackData({
          reason: 'pattern_unregistered',
          payload: rawPayload ?? payload,
          patternKey: resolvedPattern.patternKey,
        })}
      />
    );
  }

  if (!payload) {
    const reason = isViewRouterFallbackReason(fallbackReason)
      ? fallbackReason
      : 'view_payload_missing';

    return (
      <FallbackPanelView
        data={buildFallbackData({
          reason,
          payload: rawPayload ?? payload,
          patternKey: resolvedPattern.patternKey,
          envelopeViewType: registration.envelopeViewType,
        })}
      />
    );
  }

  if (!isEnvelopeLike(payload)) {
    const reason = isViewRouterFallbackReason(fallbackReason)
      ? fallbackReason
      : 'view_payload_invalid';

    return (
      <FallbackPanelView
        data={buildFallbackData({
          reason,
          payload: rawPayload ?? payload,
          patternKey: resolvedPattern.patternKey,
          envelopeViewType: registration.envelopeViewType,
        })}
      />
    );
  }

  if (isCanonicalDashboardFallbackEnvelope(payload)) {
    return <FallbackPanelView data={payload.data} hideTitle={hideTitle} />;
  }

  if (payload.view_type !== registration.envelopeViewType) {
    return (
      <FallbackPanelView
        data={buildFallbackData({
          reason: 'view_payload_mismatch',
          payload: rawPayload ?? payload,
          patternKey: resolvedPattern.patternKey,
          envelopeViewType: registration.envelopeViewType,
        })}
      />
    );
  }

  const PatternComponent = registration.Component as ComponentType<{
    data: typeof payload.data;
    hideTitle?: boolean;
  }>;

  const data = titleOverride && payload.view_type !== 'entity_summary' && payload.view_type !== 'health_gauge'
    ? { ...payload.data, title: titleOverride } : payload.data;
  return <PatternComponent data={data} hideTitle={hideTitle} />;
};
